import { ArrowRight, Check, Ear, Mic, Repeat } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { RecordingResult } from '../audio/recorder';
import { canOfferRecording, micDeclined, noteMicProblem, useMicAccess } from '../audio/useMic';
import { Compare } from '../components/Compare';
import { hasIllustration, Illustration } from '../components/Illustration';
import { PlayButton } from '../components/PlayButton';
import { RecordControl, type MicProblem } from '../components/RecordControl';
import { course, sharedStart } from '../content';
import type { Goal, Track } from '../content/types';
import { HABITS } from '../lib/habit';
import { completeOnboarding, setGoal } from '../state/store';

const STEPS = 3;
/** «Γεια σας!» in the course's own voices: the first Greek a learner hears (content/audio.json). */
const HELLO = course.voiced['onb-hello'];
const HELLO_CLIP = HELLO.audio.female ?? HELLO.audio.male!;

/**
 * The first spoken win, before any lesson: say «Γεια σας» back and hear it next to the model.
 * The microphone is asked for here, in context, by the record button's own explanation. Without
 * a microphone this simply isn't offered.
 */
function SayHello() {
  const access = useMicAccess();
  const [refused, setRefused] = useState(micDeclined);
  const [recording, setRecording] = useState<RecordingResult | null>(null);
  const [autoPlay, setAutoPlay] = useState(0);
  if (!canOfferRecording(access, refused)) return null;
  const onProblem = (problem: MicProblem) => {
    noteMicProblem(problem);
    setRefused(true);
  };
  const keep = (r: RecordingResult) => {
    setRecording(r);
    setAutoPlay((n) => n + 1);
  };
  if (!recording) return <RecordControl inline label="Now say it back" onResult={keep} onProblem={onProblem} />;
  return (
    <div className="stack reveal" style={{ gap: 6 }}>
      <p className="small muted">The model, then you. Close enough is fine.</p>
      <Compare
        clip={HELLO_CLIP}
        models={Object.values(HELLO.audio)}
        voiceName="Woman"
        recording={recording}
        autoPlay={autoPlay}
        id="onboarding-hello"
        action={<RecordControl inline label="Say it again" onResult={keep} onProblem={onProblem} />}
      />
    </div>
  );
}

function Dots({ at }: { at: number }) {
  return (
    <div className="dots" aria-hidden="true">
      {Array.from({ length: STEPS }, (_, i) => (
        <span key={i} data-on={i === at || undefined} />
      ))}
    </div>
  );
}

const COUNT = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/** Help for the undecided: how long both paths run together before they part. */
function notSure(shared: number): string {
  if (shared === 0) return 'Not sure? Pick the closer one: you can switch any time.';
  const lessons = shared === 1 ? 'lesson' : `${COUNT[shared] ?? shared} lessons`;
  return `Not sure? Both start with the same ${lessons}, so you can decide after ${shared === 1 ? 'it' : 'those'}.`;
}

/** What each goal covers, and honestly how much of it exists today. */
function goalFacts(track: Track): string {
  const ready = track.units.filter((u) => u.lessons.length > 0).length;
  const scope = track.id === 'tourist' ? `${track.units.length} short units` : `A1 to B2 · ${track.units.length} units`;
  if (ready === track.units.length) return `${scope} · all ready`;
  // A level is ready once every one of its units has lessons.
  const levels = [...new Set(track.units.flatMap((u) => (u.level ? [u.level] : [])))];
  const done = levels.filter((level) => track.units.every((u) => u.level !== level || u.lessons.length > 0));
  return `${scope} · ${done.length ? done.join(', ') : ready} ready now, the rest being written`;
}

/**
 * Three short screens: hello and how the method works; the goal, which picks the track; and an
 * if-then plan for when to practise (planning the cue helps a habit form more than reminders do).
 */
export function Onboarding() {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [goal, pickGoal] = useState<Goal | null>(null);
  const [habit, setHabit] = useState<string | null>(HABITS[0]);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  const finish = async (choice: string | null) => {
    setBusy(true);
    await completeOnboarding(choice);
  };

  if (step === 0) {
    return (
      <main className="page onboarding">
        <Dots at={0} />
        <Illustration id={hasIllustration('welcome') ? 'welcome' : 'L1'} size="scene" priority decorative className="onboarding-hero" />
        <div className="stack" style={{ gap: 6 }}>
          <div className="hello-line">
            <h1 className="greek greek-hero" lang="el">
              Γεια σας!
            </h1>
            <PlayButton src={HELLO_CLIP.src} tag="onboarding-hello" label="Hear it" ariaLabel="Hear it: «Γεια σας», hello" />
          </div>
          <p className="lead">Hello! Let's get you speaking Greek.</p>
          <p className="subtitle">Whole phrases you'll really use, said out loud from the first day.</p>
        </div>
        <SayHello />

        <ol className="method">
          <li>
            <Ear size={22} aria-hidden="true" />
            <div>
              <p className="section-title">Listen, then say it aloud</p>
              <p className="small muted">Hear your Greek next to the model. Speaking is what makes it stick.</p>
            </div>
          </li>
          <li>
            <Mic size={22} aria-hidden="true" />
            <div>
              <p className="section-title">Recall it, don't re-read it</p>
              <p className="small muted">Trying to remember works better than reading again, even when you get it wrong.</p>
            </div>
          </li>
          <li>
            <Repeat size={22} aria-hidden="true" />
            <div>
              <p className="section-title">Back just before you'd forget</p>
              <p className="small muted">Each phrase returns at the right moment. Ten minutes a day is plenty.</p>
            </div>
          </li>
        </ol>

        <div className="stack onboarding-foot">
          <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => setStep(1)}>
            Continue <ArrowRight size={20} aria-hidden="true" />
          </button>
        </div>
      </main>
    );
  }

  if (step === 1) {
    return (
      <main className="page onboarding">
        <Dots at={1} />
        <div className="stack" style={{ gap: 6 }}>
          <h1 className="title">What's your Greek for?</h1>
          <p className="subtitle">This sets your path. You can switch any time; nothing you've learned is lost.</p>
        </div>
        <div className="goal-list" role="radiogroup" aria-label="What's your Greek for?">
          {course.tracks.map((track) => (
            <button
              key={track.id}
              type="button"
              role="radio"
              aria-checked={goal === track.id}
              aria-label={`${track.label}: ${track.tagline}`}
              aria-describedby={`goal-${track.id}-d`}
              className="goal"
              onClick={() => pickGoal(track.id)}
            >
              <Illustration id={track.cover} size="scene" decorative priority className="goal-art" />
              <span className="goal-text">
                <span className="goal-label" id={`goal-${track.id}`}>
                  {track.label}
                </span>
                <span className="goal-tagline" id={`goal-${track.id}-t`}>
                  {track.tagline}
                </span>
                <span className="goal-summary" id={`goal-${track.id}-d`}>
                  {track.summary}
                  <span className="goal-facts">{goalFacts(track)}</span>
                </span>
              </span>
              <span className="goal-check" aria-hidden="true">
                <Check size={16} strokeWidth={3} />
              </span>
            </button>
          ))}
        </div>
        <div className="stack onboarding-foot">
          <button
            type="button"
            className="btn btn-primary btn-lg btn-block"
            disabled={!goal || busy}
            onClick={async () => {
              setBusy(true);
              await setGoal(goal!);
              setBusy(false);
              setStep(2);
            }}
          >
            Continue <ArrowRight size={20} aria-hidden="true" />
          </button>
          <p className="footnote" style={{ textAlign: 'center' }}>
            {notSure(sharedStart())}
          </p>
        </div>
      </main>
    );
  }

  const choice = habit === 'custom' ? custom.trim() || null : habit;
  return (
    <main className="page onboarding">
      <Dots at={2} />
      <div className="stack" style={{ gap: 6 }}>
        <p className="eyebrow">One small plan</p>
        <h1 className="title">When will you practise?</h1>
        <p className="subtitle">Tie it to something you already do every day. It's the most reliable way to build a habit.</p>
      </div>
      <div className="choice-list" role="radiogroup" aria-label="When will you practise?">
        {HABITS.map((h) => (
          <button key={h} type="button" role="radio" aria-checked={habit === h} className="choice" onClick={() => setHabit(h)}>
            {h}
          </button>
        ))}
        <button type="button" role="radio" aria-checked={habit === 'custom'} className="choice" onClick={() => setHabit('custom')}>
          Something else
        </button>
        {habit === 'custom' && (
          <label className="stack" style={{ gap: 6 }}>
            <span className="small muted">After or when…</span>
            <input className="text-input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="After I walk the dog" maxLength={60} autoFocus />
          </label>
        )}
      </div>
      <p className="muted small">No streaks to protect and no guilt. If you miss a day, just carry on the next.</p>
      <div className="stack onboarding-foot">
        <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => finish(choice)} disabled={busy || (habit === 'custom' && !custom.trim())}>
          Start learning <ArrowRight size={20} aria-hidden="true" />
        </button>
        <button type="button" className="btn btn-ghost btn-block" onClick={() => finish(null)} disabled={busy}>
          Skip for now
        </button>
      </div>
    </main>
  );
}
